import { useEffect } from 'react';
import { clsx } from 'clsx';
import { LookupField } from '../LookupField';
import type { DocumentCreationWizardProps, WizardOption } from './MasterDocument.types';
import { useMasterDocumentTranslation } from './i18n';
import { useWizardOptionFetcher, WIZARD_LOOKUP_LABEL_FIELD, type WizardLookupRecord } from './wizardOptionLookup';

// ─── Icons ─────────────────────────────────────────────────

function XIcon(props: React.SVGProps<SVGSVGElement>) {
  return (
    <svg viewBox="0 0 20 20" fill="currentColor" {...props}>
      <path d="M6.28 5.22a.75.75 0 00-1.06 1.06L8.94 10l-3.72 3.72a.75.75 0 101.06 1.06L10 11.06l3.72 3.72a.75.75 0 101.06-1.06L11.06 10l3.72-3.72a.75.75 0 00-1.06-1.06L10 8.94 6.28 5.22z" />
    </svg>
  );
}

// ─── Option Card ───────────────────────────────────────────

function OptionCard({
  option,
  selected,
  onClick,
}: {
  option: WizardOption;
  selected: boolean;
  onClick: () => void;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={clsx(
        'flex items-start gap-3 w-full rounded-xl px-4 py-3 text-left transition-all duration-150',
        'border-2',
        'focus:outline-hidden focus:ring-2 focus:ring-primary-400 focus:ring-offset-2',
        'dark:focus:ring-dark-border-custom dark:focus:ring-offset-dark-bg-primary',
        selected
          ? 'bg-primary-100 border-primary-600 border-l-[3px] dark:bg-primary-600/10 dark:border-primary-600'
          : 'bg-bg-primary border-border-secondary hover:border-border-primary hover:bg-background-secondary dark:bg-dark-bg-primary dark:border-dark-border-primary dark:hover:bg-dark-bg-secondary',
      )}
      style={selected && option.color ? { borderLeftColor: option.color } : undefined}
    >
      {option.icon && (
        <span className="shrink-0 mt-0.5">{option.icon}</span>
      )}
      <div className="min-w-0">
        <span className={clsx(
          'text-sm font-semibold',
          selected ? 'text-primary-600 dark:text-primary-400' : 'text-content-primary dark:text-dark-content-primary',
        )}>
          {option.label}
        </span>
        {option.description && (
          <p className="text-xs text-content-secondary dark:text-dark-content-secondary mt-0.5">
            {option.description}
          </p>
        )}
      </div>
    </button>
  );
}

// ─── Paso buscable (compañía, centro de operación) ─────────

/**
 * Paso del wizard resuelto con un `LookupField` en vez de un `<select>` nativo.
 *
 * El `<select>` obliga a ENCONTRAR la opción a ojo dentro de la lista desplegada;
 * el `LookupField` deja TECLEAR para filtrar, que es lo que se necesita en cuanto
 * el usuario tiene decenas de compañías o de centros de operación —y es además el
 * control con el que ya se eligen las FK en el resto del documento
 * (`FieldRenderer`, control `searcher`), así que el wizard deja de ser la
 * excepción de la pantalla.
 *
 * Los datos NO se piden a ningún endpoint: llegan ya resueltos por props y se
 * sirven desde memoria (ver `wizardOptionLookup`).
 *
 * Vive en UN solo sitio a propósito: son dos pasos con exactamente la misma
 * configuración, y dos copias de estas props vuelven a divergir en el siguiente
 * cambio.
 */
function WizardOptionLookup({
  entity,
  label,
  options,
  value,
  onChange,
  placeholder,
  disabled,
}: {
  entity: string;
  label: string;
  options: WizardOption[];
  value: string | undefined;
  onChange: (id: string) => void;
  placeholder: string;
  disabled?: boolean;
}) {
  const fetcher = useWizardOptionFetcher(options);
  return (
    <LookupField<WizardLookupRecord>
      /*
       * `entity` es un nombre SINTÉTICO, no una entidad del backend: aquí no hay
       * endpoint al que apuntar porque los datos ya vienen por props. LookupField
       * lo exige y lo usa en dos sitios —lo pasa al `fetcher` (que lo ignora) y lo
       * mete en las dependencias de sus callbacks de carga—, así que lo único que
       * se le pide es ser estable y distinto entre los dos pasos.
       */
      entity={entity}
      /*
       * El label VISIBLE lo pinta el paso del wizard, con el mismo estilo que los
       * pasos que siguen siendo `select`; el del componente se oculta con la clase
       * que LookupField expone justo para los hosts que ponen su propia etiqueta.
       *
       * El TEXTO sí se pasa: la raíz lo referencia con `aria-labelledby`, y el
       * nombre accesible se computa igual desde un nodo oculto cuando se llega a
       * él por esa vía. El `<select>` anterior no tenía ninguno —ni `id`/`htmlFor`
       * ni `aria-label`—, así que el control GANA nombre accesible, no lo pierde.
       */
      label={label}
      className="lookup-field--label-hidden"
      displayFields={[WIZARD_LOOKUP_LABEL_FIELD]}
      fetcher={fetcher}
      value={value ?? null}
      onChange={(record) => onChange(record ? String(record.Id) : '')}
      placeholder={placeholder}
      /*
       * El `<select>` no permitía volver al vacío: su opción de placeholder era
       * `disabled`. El paso es obligatorio, así que sin botón de limpiar se
       * conserva esa regla en lugar de abrir un estado que el wizard no sabe
       * representar (y que dejaría `canConfirm` en falso sin explicar por qué).
       */
      clearable={false}
      /*
       * El asterisco lo pinta el wizard junto a su propia etiqueta, así que el
       * indicador del componente se apaga —vive dentro del label oculto—. Pero
       * `required` se conserva: es lo que pone `aria-required` en la raíz, y eso
       * no es decoración (ADR-027 §Apéndice C: la semántica no se mueve con lo
       * cosmético).
       */
      required
      showRequiredIndicator={false}
      disabled={disabled}
    />
  );
}

// ─── Skeleton Loader ───────────────────────────────────────

function StepSkeleton() {
  return (
    <div className="grid grid-cols-2 gap-3">
      {Array.from({ length: 4 }).map((_, i) => (
        <div key={i} className="h-16 rounded-xl bg-gradient-to-r from-background-secondary to-background-secondary animate-pulse" />
      ))}
    </div>
  );
}

// ─── DocumentCreationWizard ────────────────────────────────

/**
 * Asistente paso a paso para crear un nuevo documento maestro.
 *
 * Guía al usuario a seleccionar compañía, centro de operación y tipo de
 * documento en tres pasos secuenciales. Soporta estados de carga y error
 * por paso, y confirma la creación cuando todos los pasos están completos.
 *
 * Cuando el consumidor pasa `documentClasses`, aparece un cuarto paso —la clase
 * del documento, entre compañía y centro de operación— para las superficies que
 * sirven a más de una clase. Es opt-in: sin ese prop, el render y el
 * comportamiento son los de siempre.
 *
 * @see docs/colors.md - Sistema de tokens de color
 * @see MasterDocument.types - DocumentCreationWizardProps, WizardOption
 *
 * @example
 * ```tsx
 * <DocumentCreationWizard
 *   open={showWizard}
 *   onClose={() => setShowWizard(false)}
 *   companies={[{ id: '1', label: 'Siesa SAS' }]}
 *   onSelectCompany={(id) => setCompany(id)}
 *   onConfirm={() => createDocument()}
 *   {...otherProps}
 * />
 * ```
 */
export function DocumentCreationWizard({
  open,
  onClose,
  companies,
  selectedCompany,
  onSelectCompany,
  documentClasses,
  selectedDocumentClass,
  onSelectDocumentClass,
  operationCenters,
  selectedCO,
  onSelectCO,
  documentTypes,
  selectedType,
  onSelectType,
  onConfirm,
  loadingStep,
  errorStep,
  className,
}: DocumentCreationWizardProps) {
  const { t } = useMasterDocumentTranslation();
  // Close on Escape
  useEffect(() => {
    if (!open) return;
    function handleKey(e: KeyboardEvent) {
      if (e.key === 'Escape') onClose();
    }
    document.addEventListener('keydown', handleKey);
    return () => document.removeEventListener('keydown', handleKey);
  }, [open, onClose]);

  if (!open) return null;

  // ── Paso de clase: opt-in ────────────────────────────────
  // Sin `documentClasses` (ausente o vacío) NADA de lo que sigue se activa: no se
  // renderiza el bloque, `canConfirm` mantiene sus tres términos, el centro de
  // operación conserva su única condición de deshabilitado y los índices de paso
  // siguen siendo 1/2/3. Es la garantía para los consumidores ya en producción.
  const hasClassStep = !!documentClasses && documentClasses.length > 0;

  // Los índices de paso son POSICIONALES: cuando la clase está activa toma el 2 y
  // corre el centro de operación al 3 y el tipo al 4.
  const coStepIndex = hasClassStep ? 3 : 2;
  const typeStepIndex = hasClassStep ? 4 : 3;

  const canConfirm = !!selectedCompany && !!selectedCO && !!selectedType
    && (!hasClassStep || !!selectedDocumentClass);

  return (
    <>
      {/* Overlay */}
      <div
        className="fixed inset-0 z-[300] bg-black/30 animate-[fadeIn_150ms_ease-out]"
        onClick={onClose}
        aria-hidden="true"
      />

      {/* Modal */}
      <div
        className={clsx(
          'fixed z-[301] top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2',
          'w-[calc(100%-32px)] max-w-[620px] max-h-[85vh] overflow-hidden',
          'bg-bg-primary rounded-2xl',
          'shadow-lg',
          'animate-[fadeIn_150ms_ease-out]',
          'dark:bg-dark-bg-primary dark:shadow-none dark:border dark:border-dark-border-primary',
          className,
        )}
        role="dialog"
        aria-modal="true"
        aria-label={t('master_document.wizard.aria')}
      >
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-border-secondary dark:border-dark-border-primary">
          <div>
            <h2 className="text-lg font-bold text-content-primary dark:text-dark-content-primary">
              {t('master_document.wizard.aria')}
            </h2>
            <p className="text-sm text-content-secondary dark:text-dark-content-secondary mt-0.5">
              {t('master_document.wizard.subtitle')}
            </p>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="p-1.5 rounded-md hover:bg-background-secondary text-content-secondary transition-all duration-150 focus:outline-hidden focus:ring-2 focus:ring-primary-400 focus:ring-offset-2 dark:focus:ring-dark-border-custom dark:focus:ring-offset-dark-bg-primary dark:hover:bg-dark-bg-secondary"
            aria-label={t('master_document.chrome.close')}
          >
            <XIcon className="w-5 h-5" />
          </button>
        </div>

        {/* Body */}
        <div className="overflow-y-auto px-6 py-5 flex flex-col gap-5" style={{ maxHeight: 'calc(85vh - 140px)' }}>
          {/* Step 1: Company */}
          <div>
            <label className="block text-xs font-semibold uppercase tracking-[0.06em] text-content-secondary mb-2">
              {t('master_document.wizard.company')} <span className="text-error-content">*</span>
            </label>
            {loadingStep === 1 ? (
              <StepSkeleton />
            ) : errorStep?.step === 1 ? (
              <StepError message={errorStep.message} onRetry={errorStep.onRetry} />
            ) : (
              <WizardOptionLookup
                entity="wizard-companies"
                label={t('master_document.wizard.company')}
                options={companies}
                value={selectedCompany}
                onChange={onSelectCompany}
                placeholder={t('master_document.wizard.selectCompany')}
              />
            )}
          </div>

          {/* Step 2 (opcional): Document Class */}
          {/* Estructuralmente igual al bloque de compañía: un `select` que no
              depende de ningún paso anterior. Las etiquetas de clase son frases
              largas ("Nota crédito de venta de solo valor"), así que la grilla de
              OptionCard del paso de tipo no encaja aquí. */}
          {hasClassStep && (
            <div>
              <label className="block text-xs font-semibold uppercase tracking-[0.06em] text-content-secondary mb-2">
                {t('master_document.wizard.documentClass')} <span className="text-error-content">*</span>
              </label>
              {loadingStep === 2 ? (
                <StepSkeleton />
              ) : errorStep?.step === 2 ? (
                <StepError message={errorStep.message} onRetry={errorStep.onRetry} />
              ) : (
                <select
                  className="w-full rounded-md border border-border-primary bg-bg-primary px-3 py-2.5 text-sm text-content-primary focus:outline-hidden focus:border-primary-600 focus:ring-2 focus:ring-primary-400 focus:ring-offset-2 dark:border-dark-border-primary dark:bg-dark-bg-primary dark:text-dark-content-primary"
                  value={selectedDocumentClass ?? ''}
                  onChange={(e) => onSelectDocumentClass?.(e.target.value)}
                  aria-label={t('master_document.wizard.documentClass')}
                >
                  <option value="" disabled>{t('master_document.wizard.selectDocumentClass')}</option>
                  {documentClasses.map((dc) => (
                    <option key={dc.id} value={dc.id}>{dc.label}</option>
                  ))}
                </select>
              )}
            </div>
          )}

          {/* Step 2 (o 3 con paso de clase): Operation Center */}
          <div>
            <label className="block text-xs font-semibold uppercase tracking-[0.06em] text-content-secondary mb-2">
              {t('master_document.wizard.operationCenter')} <span className="text-error-content">*</span>
            </label>
            {loadingStep === coStepIndex ? (
              <StepSkeleton />
            ) : errorStep?.step === coStepIndex ? (
              <StepError message={errorStep.message} onRetry={errorStep.onRetry} />
            ) : (
              <WizardOptionLookup
                entity="wizard-operation-centers"
                label={t('master_document.wizard.operationCenter')}
                options={operationCenters}
                value={selectedCO}
                onChange={onSelectCO}
                placeholder={t('master_document.wizard.selectOperationCenter')}
                disabled={!selectedCompany || (hasClassStep && !selectedDocumentClass)}
              />
            )}
          </div>

          {/* Step 3 (o 4 con paso de clase): Document Type */}
          <div>
            <label className="block text-xs font-semibold uppercase tracking-[0.06em] text-content-secondary mb-2">
              {t('master_document.wizard.documentType')} <span className="text-error-content">*</span>
            </label>
            {loadingStep === typeStepIndex ? (
              <StepSkeleton />
            ) : errorStep?.step === typeStepIndex ? (
              <StepError message={errorStep.message} onRetry={errorStep.onRetry} />
            ) : !selectedCO ? (
              <p className="text-xs text-content-secondary italic">
                {t('master_document.wizard.selectOperationCenterHint')}
              </p>
            ) : (
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                {documentTypes.map((dt) => (
                  <OptionCard
                    key={dt.id}
                    option={dt}
                    selected={dt.id === selectedType}
                    onClick={() => onSelectType(dt.id)}
                  />
                ))}
              </div>
            )}
          </div>
        </div>

        {/* Footer */}
        <div className="flex items-center justify-end gap-3 px-6 py-4 border-t border-border-secondary dark:border-dark-border-primary">
          <button
            type="button"
            onClick={onClose}
            className="px-4 py-2 rounded-md text-sm font-semibold text-content-tertiary hover:bg-background-secondary transition-all duration-150 focus:outline-hidden focus:ring-2 focus:ring-primary-400 focus:ring-offset-2 dark:focus:ring-dark-border-custom dark:focus:ring-offset-dark-bg-primary dark:text-dark-content-secondary dark:hover:bg-dark-bg-secondary"
          >
            {t('master_document.chrome.cancel')}
          </button>
          <button
            type="button"
            onClick={onConfirm}
            disabled={!canConfirm}
            className={clsx(
              'px-5 py-2 rounded-md text-sm font-semibold transition-all duration-150 min-h-[36px]',
              'focus:outline-hidden focus:ring-2 focus:ring-primary-400 focus:ring-offset-2',
              'dark:focus:ring-dark-border-custom dark:focus:ring-offset-dark-bg-primary',
              canConfirm
                ? 'bg-primary-inverse-background text-white hover:bg-primary-700 dark:bg-dark-bg-inverse dark:text-dark-content-inverse dark:hover:bg-dark-bg-inverse/90'
                : 'bg-border-primary text-content-secondary cursor-not-allowed dark:bg-dark-bg-secondary dark:text-dark-content-secondary',
            )}
          >
            {t('master_document.statusHistory.action.create')}
          </button>
        </div>
      </div>
    </>
  );
}

// ─── Step Error ────────────────────────────────────────────

function StepError({ message, onRetry }: { message: string; onRetry: () => void }) {
  const { t } = useMasterDocumentTranslation();
  return (
    <div className="flex items-center gap-3 px-4 py-3 rounded-xl bg-error-bg dark:bg-error-content/10">
      <span className="text-xs text-error-content">{message}</span>
      <button
        type="button"
        onClick={onRetry}
        className="px-3 py-1 rounded-md text-xs font-semibold text-error-content border border-error-border/30 hover:bg-error-content/10 transition-all duration-150 shrink-0 focus:outline-hidden focus:ring-2 focus:ring-primary-400 focus:ring-offset-2"
      >
        {t('master_document.chrome.retry')}
      </button>
    </div>
  );
}
